import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Users,
  FileText,
  TrendingUp,
  Shield,
  Clock,
  MessageSquare,
  ArrowRight,
  CheckCircle,
  BarChart3,
  Lock
} from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Users className="w-10 h-10 text-[#D4AF37]" />,
      title: 'Multi-Portal Access',
      description: 'Separate secure portals for admins, referring doctors, and patients with role-based permissions.'
    },
    {
      icon: <FileText className="w-10 h-10 text-[#D4AF37]" />,
      title: 'Referral Tracking',
      description: 'Complete lifecycle management from referral submission to completion with real-time updates.'
    },
    {
      icon: <MessageSquare className="w-10 h-10 text-[#D4AF37]" />,
      title: 'Secure Communication',
      description: 'HIPAA-compliant messaging and file sharing with AI-powered assistance.'
    },
    {
      icon: <Lock className="w-10 h-10 text-[#D4AF37]" />,
      title: 'Deal Rooms',
      description: 'Secure collaborative spaces for managing complex cases and treatment plans.'
    },
    {
      icon: <BarChart3 className="w-10 h-10 text-[#D4AF37]" />,
      title: 'Analytics Dashboard',
      description: 'Comprehensive insights into referral patterns, practice growth, and relationship metrics.'
    },
    {
      icon: <Shield className="w-10 h-10 text-[#D4AF37]" />,
      title: 'HIPAA Compliant',
      description: 'Enterprise-grade security with full compliance for protected health information.'
    }
  ];

  const benefits = [
    'Streamline B2B relationships with referring dentists',
    'Reduce administrative overhead by 60%',
    'Improve patient experience and outcomes',
    'Real-time collaboration and communication',
    'Comprehensive LMS for training and education',
    'Community features for engagement'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A1628] via-[#1a2332] to-[#2d3e50] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#D4AF37] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-block">
                <span className="px-4 py-2 bg-[#D4AF37]/20 text-[#D4AF37] rounded-full text-sm font-medium backdrop-blur-sm">
                  Next-Generation Referral Management
                </span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                Transform Your
                <span className="block text-[#D4AF37] mt-2">Dental Practice Network</span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                The complete platform for dental referral and network management. 
                Streamline connections between specialists, referring dentists, and patients for exceptional care delivery.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/register')}
                  className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B] text-lg px-8 py-6 group"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="text-white border-white hover:bg-white/10 text-lg px-8 py-6"
                >
                  View Demo
                </Button>
              </div>

              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-sm">Setup in 5 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-sm">HIPAA Compliant</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <Card className="bg-white/10 backdrop-blur-lg border-white/20 shadow-2xl">
                  <CardContent className="p-8">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-300">Total Referrals</p>
                          <p className="text-4xl font-bold text-white">156</p>
                        </div>
                        <TrendingUp className="w-12 h-12 text-[#D4AF37]" />
                      </div>
                      <div className="h-px bg-white/20"></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-300">Pending</p>
                          <p className="text-2xl font-bold text-white">23</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-300">Completed</p>
                          <p className="text-2xl font-bold text-[#D4AF37]">115</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">
              Everything You Need to Manage Your Dental Network
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive platform designed for dental practices, specialists, and referring providers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
              >
                <CardContent className="p-8">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#0A1628] mb-6">
                Why Choose ReferBridge?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Built to strengthen professional relationships, streamline referrals, and improve patient care across your entire dental network.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <Button
                size="lg"
                onClick={() => navigate('/register')}
                className="mt-8 bg-[#0A1628] text-[#D4AF37] hover:bg-[#1a2332]"
              >
                Get Started Today
              </Button>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#0A1628] to-[#2d3e50] rounded-2xl p-8 shadow-2xl">
                <div className="space-y-6 text-white">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold text-[#0A1628]">95%</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">Referral Success Rate</p>
                      <p className="text-lg font-semibold">Industry Leading</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold text-[#0A1628]">60%</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">Time Saved</p>
                      <p className="text-lg font-semibold">Admin Overhead</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center">
                      <span className="text-2xl font-bold text-[#0A1628]">24/7</span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">Platform Access</p>
                      <p className="text-lg font-semibold">Always Available</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-24 bg-gradient-to-br from-[#0A1628] to-[#1a2332]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Trusted by Healthcare Professionals Nationwide
            </h2>
            <p className="text-xl text-gray-300">
              Join thousands of practices improving patient care
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D4AF37] mb-2">5M+</div>
              <p className="text-gray-300 text-lg">Patient Encounters</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D4AF37] mb-2">1.2M+</div>
              <p className="text-gray-300 text-lg">Unique Patients</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D4AF37] mb-2">35K+</div>
              <p className="text-gray-300 text-lg">Healthcare Providers</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D4AF37] mb-2">1,150+</div>
              <p className="text-gray-300 text-lg">Medical Facilities</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">
              What Healthcare Professionals Say
            </h2>
            <p className="text-xl text-gray-600">
              Real results from practices like yours
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#D4AF37]">
                    {'★★★★★'.split('').map((star, i) => <span key={i} className="text-2xl">{star}</span>)}
                  </div>
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "ReferBridge has transformed how we manage referrals. The streamlined workflow reduced our administrative overhead by 60% and improved patient satisfaction significantly."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-[#0A1628] rounded-full flex items-center justify-center text-[#D4AF37] font-bold mr-4">
                    JM
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A1628]">Dr. Jennifer Martinez</p>
                    <p className="text-sm text-gray-600">Oral Surgery Specialist</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#D4AF37]">
                    {'★★★★★'.split('').map((star, i) => <span key={i} className="text-2xl">{star}</span>)}
                  </div>
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "The analytics and reporting features help us make data-driven decisions. We've seen a 40% increase in referral conversion rates since implementing ReferBridge."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-[#0A1628] rounded-full flex items-center justify-center text-[#D4AF37] font-bold mr-4">
                    RC
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A1628]">Dr. Robert Chen</p>
                    <p className="text-sm text-gray-600">Periodontics Practice Owner</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#D4AF37]">
                    {'★★★★★'.split('').map((star, i) => <span key={i} className="text-2xl">{star}</span>)}
                  </div>
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "Patient engagement has never been better. The secure messaging and real-time updates keep everyone informed throughout the entire referral process."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-[#0A1628] rounded-full flex items-center justify-center text-[#D4AF37] font-bold mr-4">
                    SK
                  </div>
                  <div>
                    <p className="font-semibold text-[#0A1628]">Sarah Kim, RDH</p>
                    <p className="text-sm text-gray-600">Practice Manager</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-[#0A1628] mb-2">
              Enterprise-Grade Security & Compliance
            </h3>
            <p className="text-gray-600">
              Your data is protected with industry-leading standards
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-12">
            <div className="flex flex-col items-center">
              <Shield className="w-16 h-16 text-[#D4AF37] mb-2" />
              <p className="font-semibold text-[#0A1628]">HIPAA Compliant</p>
            </div>
            <div className="flex flex-col items-center">
              <Lock className="w-16 h-16 text-[#D4AF37] mb-2" />
              <p className="font-semibold text-[#0A1628]">SOC 2 Certified</p>
            </div>
            <div className="flex flex-col items-center">
              <Shield className="w-16 h-16 text-[#D4AF37] mb-2" />
              <p className="font-semibold text-[#0A1628]">Medicare Compliant</p>
            </div>
            <div className="flex flex-col items-center">
              <Lock className="w-16 h-16 text-[#D4AF37] mb-2" />
              <p className="font-semibold text-[#0A1628]">256-bit Encryption</p>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">
              Seamless Integrations
            </h2>
            <p className="text-xl text-gray-600">
              Connect with your existing tools and workflows
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#0A1628] rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A1628] mb-2">EHR/EMR Systems</h3>
                <p className="text-gray-600">
                  Seamlessly integrate with Epic, Cerner, Athenahealth, and more
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#0A1628] rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A1628] mb-2">Communication Tools</h3>
                <p className="text-gray-600">
                  Direct Secure Messaging, fax systems, and telehealth platforms
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#0A1628] rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0A1628] mb-2">Analytics & Billing</h3>
                <p className="text-gray-600">
                  CPT/CMS billing codes, revenue cycle management, and reporting
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#0A1628] to-[#2d3e50] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join leading dental practices using ReferBridge to streamline operations and grow their network.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B] text-lg px-8"
            >
              Start Your Free Trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/contact')}
              className="text-white border-white hover:bg-white/10 text-lg px-8"
            >
              Schedule a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/logo-4.png" alt="ReferBridge" className="h-10 w-auto" />
              </div>
              <p className="text-gray-400">
                Dental Referral and Network Management Platform
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-400 hover:text-[#D4AF37]">Features</a></li>
                <li><a href="/pricing" className="text-gray-400 hover:text-[#D4AF37]">Pricing</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#D4AF37]">Integrations</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#D4AF37]">Security</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="/about" className="text-gray-400 hover:text-[#D4AF37]">About Us</a></li>
                <li><a href="/contact" className="text-gray-400 hover:text-[#D4AF37]">Contact</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#D4AF37]">Careers</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#D4AF37]">Blog</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-[#D4AF37]">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#D4AF37]">Terms of Service</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#D4AF37]">HIPAA Compliance</a></li>
                <li><a href="#" className="text-gray-400 hover:text-[#D4AF37]">Cookie Policy</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>&copy; 2025 ReferBridge. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
